// Confluence engine — the pure core of the generalised watchlist ("confluence
// board"). A BOARD is an ordered list of CARDS; each card is a security plus its
// own setup: a set of COLUMNS (a reading on a timeframe), an agreement RULE, and
// oversold/overbought thresholds. Everything here is pure and unit-tested; the
// UI/store layers fetch candles and persist. Readings reuse the shipped,
// already-tested indicator math in chart-indicators.ts.
//
// Provenance: sandbox/confluence-board prototype (design), generalised from the
// original Bollinger-across-timeframes watchlist (the founder's wife's thesis).

import type { OHLCPoint } from "./market-data.js";
import { bollingerBandSeries, rsiSeries, emaValues, stochasticSeries, macdSeries, atrSeries } from "./chart-indicators.js";

// Readings are all normalised 0..1 with oversold→0, overbought→1 so they share
// the same agreement/verdict logic. Two families beyond the original oscillators:
//   • statistical extremes — where price sits in its OWN recent distribution
//     ("z" z-score, "pct" percentile) or how fast it moved ("roc").
//   • structural / price-action — where price sits in its OWN recent geometry
//     ("range" = position in the last-N high/low channel).
// All are self-referential (computed from one instrument's candles), which is
// why they slot straight into the existing board/scan/UI. Cross-sectional and
// intermarket readings are NOT here — they need many instruments at once and
// belong to a separate relational engine.
export type ReadingKey = "bb" | "rsi" | "stoch" | "ema" | "pct" | "z" | "roc" | "range" | "sr" | "trend" | "macd" | "clv" | "eff";
export const READING_KEYS: ReadingKey[] = ["bb", "rsi", "stoch", "ema", "pct", "z", "roc", "range", "sr", "trend", "macd", "clv", "eff"];
export const SHORT: Record<ReadingKey, string> = { bb: "%B", rsi: "RSI", stoch: "Stoch", ema: "EMA", pct: "%ile", z: "Z", roc: "ROC", range: "Range", sr: "S/R", trend: "Trend", macd: "MACD", clv: "Close", eff: "Eff" };
export const READING_LABEL: Record<ReadingKey, string> = {
  bb: "Bollinger %B", rsi: "RSI", stoch: "Stochastic", ema: "% from EMA", pct: "Price percentile",
  z: "Z-score", roc: "Momentum (ROC)", range: "Range position", sr: "Support / resistance", trend: "Trend structure", macd: "MACD momentum", clv: "Close strength", eff: "Trend efficiency",
};

/** Readings grouped by the self-referential category that offers them (D-096), so a
 * category's settings can let the user pick WITHIN its family instead of a single
 * fixed reading. "indicator" keeps the full palette; these two are the scoped
 * families. (Structural has one member today; more price-action readings land here
 * next, at which point its picker earns its place too.) */
export const CAT_FAMILY: Record<"statistical" | "structural", ReadingKey[]> = {
  statistical: ["z", "pct", "roc"],
  structural: ["range", "sr", "trend", "clv", "eff"],
};

/** Plain-English explainer per reading, shown by the unfolding ⓘ notes (D-096):
 * what it is, what a low (blue) vs high (purple) value means, and what traders
 * generally look for. Kept in the engine so every surface explains a reading the
 * same way. Progressive disclosure — short and jargon-light on purpose. */
export interface ReadingInfo { what: string; low: string; high: string; lookFor: string; }
export const READING_INFO: Record<ReadingKey, ReadingInfo> = {
  bb: {
    what: "Where price sits inside its Bollinger Bands — its normal volatility envelope.",
    low: "Near or below the lower band — unusually cheap versus recent volatility.",
    high: "Near or above the upper band — unusually stretched to the upside.",
    lookFor: "Fade the extremes for a snap-back; or watch price ‘walk the band’ in a strong trend.",
  },
  rsi: {
    what: "Momentum oscillator (0–100): the size of recent gains versus recent losses.",
    low: "Below ~30 — oversold; the selling may be getting overdone.",
    high: "Above ~70 — overbought; the buying may be getting overdone.",
    lookFor: "Extremes for reversals, the 50 line for trend bias, and divergence against price.",
  },
  stoch: {
    what: "Where the close sits within its recent high–low range (0–100).",
    low: "Near 0 — closing at the bottom of the range.",
    high: "Near 100 — closing at the top of the range.",
    lookFor: "Turns out of the extremes, especially when %K crosses %D.",
  },
  ema: {
    what: "How far price has stretched above or below its moving average.",
    low: "Well below the average — extended to the downside.",
    high: "Well above the average — extended to the upside.",
    lookFor: "Snap-backs to the average when price is far from it; which side price sits for trend.",
  },
  pct: {
    what: "Where today’s price ranks within its own recent history — the share of recent closes it’s above.",
    low: "Near 0% — close to the cheapest it’s been recently.",
    high: "Near 100% — close to the dearest it’s been recently.",
    lookFor: "How stretched it is versus its own range: extremes to fade, persistence to follow.",
  },
  z: {
    what: "How many standard deviations price is from its own recent average — a pure ‘how unusual is this?’ measure.",
    low: "Strongly negative (say −2σ) — unusually far below normal; a rare cheap reading.",
    high: "Strongly positive (+2σ) — unusually far above normal; a rare rich reading.",
    lookFor: "Big readings either way flag an outlier that often reverts; near zero is just ‘normal’.",
  },
  roc: {
    what: "Rate of change — how fast price has moved over the window, i.e. momentum.",
    low: "Strongly falling — downside momentum.",
    high: "Strongly rising — upside momentum.",
    lookFor: "Confirming trend strength, spotting exhaustion at extremes, and divergence against price.",
  },
  range: {
    what: "Where price sits in its recent high–low channel — the structural range.",
    low: "At the bottom of the range — testing support.",
    high: "At the top of the range — testing resistance, near a breakout.",
    lookFor: "Bounces at range lows, breakouts at range highs — where you are between structure.",
  },
  sr: {
    what: "Where price sits between the nearest swing support below and swing resistance above.",
    low: "Down at support — the recent floor buyers have defended.",
    high: "Up at resistance — the recent ceiling sellers have defended.",
    lookFor: "Bounces off support, rejections at resistance, and breaks that flip one into the other.",
  },
  trend: {
    what: "Dow-style trend from the swing sequence: is price making higher highs & higher lows, or lower highs & lower lows?",
    low: "Lower highs and lower lows — a downtrend structure.",
    high: "Higher highs and higher lows — an uptrend structure.",
    lookFor: "Trade with the structure; treat the first break of it (a higher low failing, say) as an early turn.",
  },
  macd: {
    what: "MACD — the gap between a fast and a slow moving average; a classic momentum gauge.",
    low: "Fast average below the slow one — bearish momentum.",
    high: "Fast average above the slow one — bullish momentum.",
    lookFor: "The zero line for bias, and crosses/turns for momentum shifting gear.",
  },
  clv: {
    what: "Close strength — where price tends to close inside each bar’s own high–low range, averaged over the window. It shows who keeps finishing in control.",
    low: "Bars keep closing near their lows — sellers finishing on top.",
    high: "Bars keep closing near their highs — buyers finishing on top.",
    lookFor: "Strong closes off support can confirm a bounce; weak closes into resistance can warn of a stall.",
  },
  eff: {
    what: "Trend efficiency — how much of price’s total back-and-forth travel actually got somewhere. A smooth one-way move scores near the ends; a choppy market that ends up where it started scores in the middle.",
    low: "A clean move down — most of the travel was one-way lower.",
    high: "A clean move up — most of the travel was one-way higher.",
    lookFor: "Near the ends the trend is clean and worth following; a middling reading is chop, where trends stall and fades tend to do better.",
  },
};

/** Timeframes offered in the readings × timeframes picker (same set as the
 * prototype; fetch mapping lives in confluence-data.ts). */
export const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1D", "1W", "1M", "3M", "6M", "1Y"];

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Fractal swing pivots over a window: a high/low that is the STRICT extreme of
 * its ±K neighbours (ties disqualify, so flats don't count). Returned in time
 * order. Shared by the structural readings (support/resistance, trend). */
function swingPivots(w: OHLCPoint[], K: number): { highs: number[]; lows: number[] } {
  const highs: number[] = [];
  const lows: number[] = [];
  for (let i = K; i < w.length - K; i++) {
    let isHigh = true;
    let isLow = true;
    for (let j = i - K; j <= i + K; j++) {
      if (j === i) continue;
      if (w[j]!.high >= w[i]!.high) isHigh = false;
      if (w[j]!.low <= w[i]!.low) isLow = false;
    }
    if (isHigh) highs.push(w[i]!.high);
    if (isLow) lows.push(w[i]!.low);
  }
  return { highs, lows };
}

/** One computed cell: a normalised 0..1 reading plus its scale-end labels
 * (bottom/left, top/right). `v` is null when there isn't enough data. */
export interface Cell {
  v: number | null;
  ends: [string, string];
}

/** Evaluate one reading on a lens's candles → normalised 0..1 (oversold→0, overbought→1). */
/** Natural lookback (bars) for the windowed self-referential readings — the window
 * each one uses when the card doesn't override it. Kept here so the engine defaults
 * and the settings slider's starting value stay in one place (D-096). The indicator
 * readings (bb/rsi/stoch/ema/macd) have their own fixed periods and ignore this. */
export const READING_LOOKBACK: Partial<Record<ReadingKey, number>> = { pct: 60, z: 20, roc: 20, range: 20, sr: 60, trend: 60, clv: 20, eff: 20 };

/** Default period for the classic indicators that take one — the length each uses
 * when a column doesn't override it (D-096). Only these four accept a period; MACD
 * has three lengths and the self-referential readings use a lookback instead. */
export const READING_PERIOD: Partial<Record<ReadingKey, number>> = { bb: 20, rsi: 14, stoch: 14, ema: 21 };

/** Evaluate one reading over a candle series → a normalised 0..1 Cell (0 = oversold /
 * low, 1 = overbought / high). `opts.lookback` overrides the window for the
 * self-referential readings; unset falls back to READING_LOOKBACK (so behaviour is
 * unchanged unless the user tunes it). */
export function evalReading(key: ReadingKey, candles: OHLCPoint[], opts?: { lookback?: number; period?: number }): Cell {
  const lb = (dflt: number, min = 2): number => Math.max(min, Math.round(opts?.lookback ?? dflt));
  const pd = (dflt: number): number => Math.max(2, Math.round(opts?.period ?? dflt));
  switch (key) {
    case "bb": {
      const s = bollingerBandSeries(candles, pd(READING_PERIOD.bb!), 2);
      const last = s[s.length - 1];
      if (!last || last.upper <= last.lower || !candles.length) return { v: null, ends: ["—", "—"] };
      const price = candles[candles.length - 1]!.close;
      return { v: clamp01((price - last.lower) / (last.upper - last.lower)), ends: [last.lower.toFixed(2), last.upper.toFixed(2)] };
    }
    case "rsi": {
      const s = rsiSeries(candles, pd(READING_PERIOD.rsi!));
      const last = s[s.length - 1];
      return { v: last ? clamp01(last.value / 100) : null, ends: ["0", "100"] };
    }
    case "stoch": {
      const s = stochasticSeries(candles, pd(READING_PERIOD.stoch!), 3);
      const last = s[s.length - 1];
      return { v: last ? clamp01(last.k / 100) : null, ends: ["0", "100"] };
    }
    case "ema": {
      const vals = emaValues(candles, pd(READING_PERIOD.ema!));
      const e = vals[vals.length - 1];
      if (e == null || !candles.length) return { v: null, ends: ["below", "above"] };
      const price = candles[candles.length - 1]!.close;
      const d = (price - e) / e;
      return { v: clamp01(0.5 + Math.tanh(d * 12) / 2), ends: ["below", "above"] };
    }
    case "pct": {
      if (candles.length < 2) return { v: null, ends: ["0%", "100%"] };
      const w = candles.slice(-lb(READING_LOOKBACK.pct!)).map((c) => c.close);
      const last = w[w.length - 1]!;
      return { v: clamp01(w.filter((x) => x <= last).length / w.length), ends: ["0%", "100%"] };
    }
    case "z": {
      // Statistical extreme: price's z-score vs its own recent mean, squashed
      // so ±3σ maps to the 0..1 ends. Far below mean → oversold.
      const w = candles.slice(-lb(READING_LOOKBACK.z!)).map((c) => c.close);
      if (w.length < 2) return { v: null, ends: ["−3σ", "+3σ"] };
      const mean = w.reduce((a, b) => a + b, 0) / w.length;
      const sd = Math.sqrt(w.reduce((a, b) => a + (b - mean) ** 2, 0) / w.length);
      if (sd === 0) return { v: null, ends: ["−3σ", "+3σ"] };
      const z = (w[w.length - 1]! - mean) / sd;
      return { v: clamp01(0.5 + Math.tanh(z / 3) / 2), ends: ["−3σ", "+3σ"] };
    }
    case "roc": {
      // Momentum: rate of change over the lookback window, squashed. Falling → 0.
      const w = candles.slice(-(lb(READING_LOOKBACK.roc!) + 1)).map((c) => c.close);
      if (w.length < 2) return { v: null, ends: ["falling", "rising"] };
      const prev = w[0]!;
      if (prev === 0) return { v: null, ends: ["falling", "rising"] };
      const r = (w[w.length - 1]! - prev) / prev;
      return { v: clamp01(0.5 + Math.tanh(r * 8) / 2), ends: ["falling", "rising"] };
    }
    case "range": {
      // Structural: position in the lookback high/low channel (Donchian %).
      // At the channel low → 0 (oversold), at the high → 1.
      const w = candles.slice(-lb(READING_LOOKBACK.range!));
      if (w.length < 2) return { v: null, ends: ["—", "—"] };
      const hi = Math.max(...w.map((c) => c.high));
      const lo = Math.min(...w.map((c) => c.low));
      if (hi <= lo) return { v: null, ends: ["—", "—"] };
      const price = candles[candles.length - 1]!.close;
      return { v: clamp01((price - lo) / (hi - lo)), ends: [lo.toFixed(2), hi.toFixed(2)] };
    }
    case "sr": {
      // Structural: where price sits between the nearest swing SUPPORT below and
      // swing RESISTANCE above. Detects fractal pivots (a high/low that's the strict
      // extreme of its ±K neighbours) over the last 60 bars, then takes the closest
      // pivot above and below the current price. At support → 0, at resistance → 1;
      // falls back to the window's own extreme when no pivot sits that side.
      const K = 2;
      const w = candles.slice(-lb(READING_LOOKBACK.sr!, 2 * K + 3));
      if (w.length < 2 * K + 3) return { v: null, ends: ["—", "—"] };
      const { highs, lows } = swingPivots(w, K);
      const price = candles[candles.length - 1]!.close;
      const resAbove = highs.filter((h) => h > price).sort((a, b) => a - b)[0];
      const supBelow = lows.filter((l) => l < price).sort((a, b) => b - a)[0];
      const res = resAbove ?? Math.max(...w.map((c) => c.high));
      const sup = supBelow ?? Math.min(...w.map((c) => c.low));
      if (res <= sup) return { v: null, ends: ["—", "—"] };
      return { v: clamp01((price - sup) / (res - sup)), ends: [sup.toFixed(2), res.toFixed(2)] };
    }
    case "trend": {
      // Structural: Dow-style trend from the swing sequence. Higher highs & higher
      // lows → up (→1); lower highs & lower lows → down (→0); mixed → 0.5.
      const K = 2;
      const w = candles.slice(-lb(READING_LOOKBACK.trend!, 2 * K + 3));
      if (w.length < 2 * K + 3) return { v: null, ends: ["down", "up"] };
      const { highs, lows } = swingPivots(w, K);
      if (highs.length < 2 || lows.length < 2) return { v: null, ends: ["down", "up"] };
      const hh = Math.sign(highs[highs.length - 1]! - highs[highs.length - 2]!);
      const ll = Math.sign(lows[lows.length - 1]! - lows[lows.length - 2]!);
      return { v: clamp01((hh + ll) / 4 + 0.5), ends: ["down", "up"] };
    }
    case "macd": {
      // Momentum: the MACD line (fast EMA − slow EMA), self-scaled to 0..1 by its
      // own recent extreme so it's price-scale independent. Persistently negative →
      // bearish (→0); persistently positive → bullish (→1). Reuses the tested
      // macdSeries; only this normalisation is new.
      const s = macdSeries(candles);
      if (!s.length) return { v: null, ends: ["bearish", "bullish"] };
      const last = s[s.length - 1]!.macd;
      const maxAbs = Math.max(...s.map((p) => Math.abs(p.macd)));
      if (maxAbs === 0) return { v: 0.5, ends: ["bearish", "bullish"] };
      return { v: clamp01(0.5 + (last / maxAbs) / 2), ends: ["bearish", "bullish"] };
    }
    case "clv": {
      // Structural / price-action: where price closes WITHIN each bar's own high–low
      // range, averaged over the window. Closing near the high (buyers finishing on
      // top) → 1; near the low (sellers on top) → 0. A flat bar (high==low) is 0.5.
      const w = candles.slice(-lb(READING_LOOKBACK.clv!));
      if (w.length < 2) return { v: null, ends: ["at lows", "at highs"] };
      const clv = w.map((c) => (c.high > c.low ? (c.close - c.low) / (c.high - c.low) : 0.5));
      return { v: clamp01(clv.reduce((a, b) => a + b, 0) / clv.length), ends: ["at lows", "at highs"] };
    }
    case "eff": {
      // Structural / price-action: Kaufman efficiency ratio, SIGNED. Net change over
      // the window ÷ the total path travelled (sum of bar-to-bar moves). A smooth
      // one-way trend → ±1; a choppy market that ends where it began → 0. Mapped so a
      // clean downtrend → 0, a clean uptrend → 1, chop → 0.5.
      const w = candles.slice(-(lb(READING_LOOKBACK.eff!) + 1)).map((c) => c.close);
      if (w.length < 3) return { v: null, ends: ["clean down", "clean up"] };
      const net = w[w.length - 1]! - w[0]!;
      let path = 0;
      for (let i = 1; i < w.length; i++) path += Math.abs(w[i]! - w[i - 1]!);
      if (path === 0) return { v: 0.5, ends: ["clean down", "clean up"] };
      return { v: clamp01(0.5 + (net / path) / 2), ends: ["clean down", "clean up"] };
    }
  }
}

// ---- board model -----------------------------------------------------------

export interface Column {
  reading: ReadingKey;
  tf: string;
  /** Optional length override for the classic indicators (bb/rsi/stoch/ema). Absent →
   * READING_PERIOD default. Additive & optional so existing boards are unchanged. */
  period?: number;
  /** Optional window override for the windowed self-referential readings (used when the
   * Reading-Lab backtests a statistical / structural setup at its chosen lookback). */
  lookback?: number;
  /** Optional PER-READING zone override (D-120). When set, this reading uses its own
   * low (os) / high (ob) threshold instead of the setup's global pair — because even
   * after normalising to 0..1, indicators reach their extremes at different rates.
   * Absent → fall back to the card's os/ob. Additive & optional. */
  os?: number;
  ob?: number;
}
/** Context gates that don't fit the confluence bars (they're not oversold↔overbought
 * directional) so they live in settings and simply SUPPRESS the card's flag until met
 * (D-096). A gate whose data is unavailable (e.g. volume on spot gold) is skipped, not
 * failed — so it never blanks a card that can't supply it. */
/** A reading-based context gate (D-115): the setup may only flag when this reading,
 * on its own timeframe, sits inside [min, max]. It's the "context that must hold" for
 * role-structured setups — e.g. Dip in an uptrend gates on Trend 1D being UP (min≈0.6)
 * and runs the oversold confluence only over the oscillator columns. Like the
 * volume/volatility gates it SUPPRESSES the flag rather than joining the k-of-n, so the
 * confluence core is untouched; a gate whose data isn't available yet is skipped, never
 * failed (never blanks a card that can't supply it). Additive & optional. */
export interface ReadingGate {
  reading: ReadingKey;
  tf: string;
  /** Reading must be ≥ min (absent = no lower bound). 0..1. */
  min?: number;
  /** Reading must be ≤ max (absent = no upper bound). 0..1. */
  max?: number;
  /** Length override for the classic indicators (bb/rsi/stoch/ema). */
  period?: number;
  /** Window override for the windowed self-referential readings. */
  lookback?: number;
}
export interface CardFilters {
  /** Only flag when latest volume ≥ this × its recent average (0 / absent = off). */
  volMin?: number;
  /** Only flag when volatility (ATR) is at or above this percentile of its own recent
   * range, 0..1 (0 / absent = off). */
  volatilityMin?: number;
  /** Reading-based context gates (all must pass) — see ReadingGate. Absent = none. */
  gates?: ReadingGate[];
}
/** The breakout engine's parameters (D-115). A breakout FIRES when price closes THROUGH a
 * real support/resistance level — the nearest SWING PIVOT (where price actually turned) in
 * the prior `lookback` bars, not the raw high/low — as a buy (through resistance) or sell
 * (through support), optionally requiring a momentum `confirm` to agree. Unlike the
 * confluence engine this is a level-CROSS event, not an extreme reading, which is why it's
 * its own engine rather than a confluence card. */
export interface BreakoutParams {
  /** Timeframe the levels (and the forward move) are measured on. */
  tf: string;
  /** How many bars back to scan for swing support/resistance. */
  lookback: number;
  /** Fraction beyond the level required to count as a break (0 = a bare close-through). */
  buffer?: number;
  /** Optional momentum confirm: the break only counts when this reading agrees with the
   * direction (≥ min for an up-break, ≤ 1−min for a down-break). Absent = structure only. */
  confirm?: { reading: ReadingKey; tf: string; min: number };
}
export interface Card {
  id: string;
  sym: string;
  cols: Column[];
  rule: "all" | "k";
  k: number;
  os: number; // oversold threshold 0..1
  ob: number; // overbought threshold 0..1
  /** Which reading the self-referential lenses (statistical / structural) show.
   * Absent → the category's default (see CAT_FAMILY[cat][0]). Additive & optional
   * so existing boards keep working unchanged. */
  catReading?: Partial<Record<"statistical" | "structural", ReadingKey>>;
  /** How many bars back the self-referential lenses look, per category. Absent →
   * each reading's own default (READING_LOOKBACK). Additive & optional. */
  catLookback?: Partial<Record<"statistical" | "structural", number>>;
  /** Non-directional context gates (volume / volatility) — see CardFilters. */
  filters?: CardFilters;
  /** Which trade type this setup IS (matches a TRADE_TYPES entry). Drives the Watch
   * pill icon and the Build structured groups. Absent → the user's "Custom" setup, so
   * existing single-card boards keep working untouched. Additive & optional. */
  name?: string;
  /** User-facing display name for the setup, shown in the Setups library — distinct
   * from `name` (the trade type). Defaults to "unnamed_N". Additive & optional. */
  label?: string;
  /** Which extreme this setup trades — "buy" watches the oversold agreement,
   * "sell" the overbought. Metadata for the Scan pills only; verdict() is
   * unchanged and still reports whichever extreme actually agrees. Additive. */
  dir?: "buy" | "sell";
  /** Which engine computes this card's verdict (D-115). Absent / "confluence" = the
   * k-of-n confluence engine (everything below — cols, rule, k, os, ob, mode). "breakout"
   * routes to the breakout engine instead (see `breakout`), whose verdict is a level-cross
   * test, not extreme-agreement. Additive & optional so every existing card is confluence. */
  engine?: "confluence" | "breakout";
  /** Parameters for the breakout engine (only read when engine === "breakout"). */
  breakout?: BreakoutParams;
  /** How agreement at an extreme maps to a trade direction (D-115).
   *   • "fade" (default, absent) — mean-reversion: oversold agreement → buy (a
   *     bounce), overbought → sell (fade the rip). This is what confluence has
   *     always done, so every legacy card is unchanged.
   *   • "follow" — with-trend/continuation: the SAME agreement computation, but
   *     the readings pointing UP together (overbought) means buy (ride it) and
   *     DOWN together (oversold) means sell. The one primitive that lets a
   *     momentum setup (e.g. Trend continuation) live on the confluence engine
   *     instead of fighting it. Additive & optional. */
  mode?: "fade" | "follow";
  /** Marks a card that was armed by a WATCHLIST-WIDE scan (D-158) — the same
   * setup fanned out live across every security in a watchlist. Purely a tag so
   * the group can be cleared in one action ("remove scan from all"); the engine,
   * scanning, and notifications treat it as any other live setup. Additive. */
  scan?: boolean;
}
export type Board = Card[];

export function newId(): string {
  return "c" + Math.random().toString(36).slice(2, 9);
}
/** The original watchlist setup: Bollinger %B across six timeframes. */
export function defaultColumns(): Column[] {
  return ["1m", "5m", "30m", "1h", "4h", "1D"].map((tf) => ({ reading: "bb" as ReadingKey, tf }));
}
export function newCard(sym: string): Card {
  return { id: newId(), sym, cols: defaultColumns(), rule: "all", k: 3, os: 0.3, ob: 0.7 };
}

/** A named starter setup the user can drop onto a security. It's just a Card
 * shape (columns + default thresholds) plus a label and intended direction; once
 * instantiated it's an ordinary per-security card the user tunes like any other.
 * The library is a starting point, NOT advice — every threshold is meant to be
 * tuned/optimised per instrument (the whole point of Tune). */
export interface TradeType {
  id: string;
  name: string;
  /** Short pill label (the tight version of `name` for the Scan pill). */
  pill: string;
  /** "buy" only lights on the oversold agreement, "sell" only on overbought.
   * Absent → symmetric: the pill lights on whichever extreme actually fires. */
  dir?: "buy" | "sell";
  /** Fade (default) or follow — see Card.mode. A continuation setup sets
   * "follow" so agreement WITH the trend reads as the trade direction. */
  mode?: "fade" | "follow";
  /** Plain-language description of what the setup IS — shown in its own box on the
   * setup's settings page, so the reader learns what makes this setup different. */
  desc: string;
  seed: Pick<Card, "cols" | "rule" | "k" | "os" | "ob" | "filters" | "engine" | "breakout">;
}

const col = (reading: ReadingKey, tf: string): Column => ({ reading, tf });

/** Starter trade-type library (D-114). Each is a confluence preset spanning a
 * lens or two; names/shapes are illustrative SEEDS to tune, never advice. These
 * are the single-symbol (card-backed) types — the cross-sectional (leader/laggard)
 * and intermarket (divergence) types read across the field and need a different
 * backing (crossEdge / intermarketPairs), so they land as a later increment. */
export const TRADE_TYPES: TradeType[] = [
  {
    id: "stretched", name: "Stretched — due a snap-back", pill: "Stretched",
    // Symmetric (dir absent): fires oversold→buy or overbought→sell.
    desc: "Mean-reversion — the idea that a market pushed unusually far from its normal range tends to snap back. It fires when several readings hit an extreme together: momentum oscillators (%B, RSI, Stochastic) plus statistical extremes (percentile, Z-score). Because it's symmetric it lights either way — a stretched low (oversold, watching for a bounce) or a stretched high (overbought, watching for a fade).",
    seed: { cols: [col("bb", "30m"), col("rsi", "1h"), col("stoch", "1h"), col("pct", "1D"), col("z", "1D")], rule: "k", k: 3, os: 0.25, ob: 0.75 },
  },
  {
    id: "dip-in-uptrend", name: "Dip in an uptrend", pill: "Dip in uptrend", dir: "buy",
    desc: "A with-trend pullback buy. It's built in two parts: a CONTEXT gate that must hold — the higher-timeframe trend up (trend structure holding on the daily, price above its EMA on the 4-hour) — and, only when that holds, a TRIGGER: short-term readings dipping to oversold (RSI, %B, Stochastic). So it fires on a shallow pause inside an uptrend, not a reversal — buying the dip while the bigger trend is still intact. The trend readings gate the setup rather than joining the oversold vote, which is why the two don't pull against each other.",
    // Gate = trend up (must hold); trigger = oscillators oversold (the confluence vote).
    seed: { cols: [col("rsi", "1h"), col("bb", "30m"), col("stoch", "30m")], rule: "k", k: 2, os: 0.35, ob: 0.65, filters: { gates: [{ reading: "trend", tf: "1D", min: 0.6 }, { reading: "ema", tf: "4h", min: 0.5 }] } },
  },
  {
    id: "breakout", name: "Breakout", pill: "Breakout",
    // Its own engine: a level-CROSS test, not extreme-agreement. Symmetric — lights on a
    // break of either edge (up = buy, down = sell). The cols below are display-only (they
    // show the channel position + momentum on the expanded board); the verdict comes from
    // breakoutVerdict.
    desc: "Price closing THROUGH a real level — the nearest swing resistance (a buy break) or swing support (a sell break) — with momentum behind it. The level is where price has actually turned before (swing pivots), not just the highest high of the last N bars, because a raw high can be a lone wick the market never respected. Unlike the other setups this isn't about a reading being at an extreme; it's the level-cross event itself, so it has its own engine. A momentum confirm keeps it from lighting on a limp poke through. Symmetric: it fires on a break of either side, only on the bar the level gives way.",
    seed: { cols: [col("range", "1h"), col("roc", "30m")], rule: "k", k: 1, os: 0.3, ob: 0.7, engine: "breakout", breakout: { tf: "1h", lookback: 20, buffer: 0, confirm: { reading: "roc", tf: "30m", min: 0.55 } } },
  },
  {
    id: "trend-continuation", name: "Trend continuation", pill: "Trend continuation", mode: "follow",
    // Symmetric + follow: with-trend in whichever direction the structure points —
    // readings strong-up together → buy (ride it), strong-down together → sell.
    desc: "A with-trend momentum entry — the opposite of Stretched. It wants trend and momentum pointing the same way across timeframes (trend structure plus MACD/ROC and EMA), and it fires WITH that strength: when the readings line up to the upside it reads as a buy, to the downside a sell. The idea is to ride a move that's still running rather than fade it — which is why it follows the readings instead of fading them.",
    seed: { cols: [col("trend", "1D"), col("trend", "4h"), col("macd", "1h"), col("roc", "1h"), col("ema", "1h")], rule: "k", k: 3, os: 0.3, ob: 0.7 },
  },
];

/** Descriptions for the two RELATIONAL setups (they aren't card-backed — they read
 * across the field — so their identity is keyed by the lens, not a card name). */
export const RELATIONAL_INFO: Record<"cross" | "inter", { title: string; desc: string }> = {
  cross: {
    title: "Leader / laggard",
    desc: "Relative strength across your watchlist. It ranks this instrument against the others on return, momentum, Z-score and range, and lights when it sits at the top of the pack (a leader) or the bottom (a laggard). It's a read about the instrument versus its peers, not on its own.",
  },
  inter: {
    title: "Divergence",
    desc: "The classic pairs read. It finds the instrument's most-correlated partner on your list and measures how far their spread has stretched from its usual level, lighting when two names that normally move together have pulled unusually far apart — the trade being that the spread snaps back.",
  },
};

/** What-is-this text for the open setup: a named card setup (by name), or a relational
 * lens (by category). Returns null for a Custom / unnamed setup. */
export function setupInfoFor(name: string | undefined, category: string): { title: string; desc: string } | null {
  if (category === "cross") return RELATIONAL_INFO.cross;
  if (category === "inter") return RELATIONAL_INFO.inter;
  const t = name ? TRADE_TYPES.find((x) => x.name === name) : undefined;
  return t ? { title: t.name, desc: t.desc } : null;
}

/** Instantiate a trade-type template as a fresh, independent card for a security.
 * The template only seeds the defaults; from here it tunes like any other card. */
export function cardFromTradeType(sym: string, t: TradeType): Card {
  return {
    id: newId(),
    sym: sym.toUpperCase(),
    cols: t.seed.cols.map((c) => ({ ...c })),
    rule: t.seed.rule,
    k: t.seed.k,
    os: t.seed.os,
    ob: t.seed.ob,
    name: t.name,
    ...(t.dir ? { dir: t.dir } : {}),
    ...(t.mode ? { mode: t.mode } : {}),
    ...(t.seed.filters ? { filters: JSON.parse(JSON.stringify(t.seed.filters)) as CardFilters } : {}),
    ...(t.seed.engine ? { engine: t.seed.engine } : {}),
    ...(t.seed.breakout ? { breakout: JSON.parse(JSON.stringify(t.seed.breakout)) as BreakoutParams } : {}),
  };
}

export interface Lens {
  reading: ReadingKey;
  tf: string;
  label: string;
  period?: number;
}
/** Minimal per-column labels: just the timeframe when the reading is constant
 * (the classic view), just the reading when the timeframe is constant, else both. */
export function lenses(card: Card): Lens[] {
  const cols = card.cols;
  if (!cols.length) return [];
  const sameR = cols.every((c) => c.reading === cols[0]!.reading);
  const sameT = cols.every((c) => c.tf === cols[0]!.tf);
  return cols.map((c) => ({
    reading: c.reading,
    tf: c.tf,
    period: c.period,
    label: sameR && !sameT ? c.tf : sameT && !sameR ? SHORT[c.reading] : SHORT[c.reading] + " " + c.tf,
  }));
}

export type Verdict = "buy" | "sell" | null;
/** A card flags when enough of its readings agree at an extreme. "all" needs
 * every column (a missing reading can't confirm "all"); "k" needs k of them.
 * The extreme→direction map depends on card.mode: "fade" (default) reads
 * oversold agreement as a buy and overbought as a sell (mean-reversion);
 * "follow" inverts it (overbought agreement = with-trend buy, oversold = sell).
 * Everything downstream — pill colour, box tint, base-rate orientation — reads
 * this one direction, so the mode flows through consistently (D-115). */
export function verdict(values: Array<number | null>, card: Card): Verdict {
  // Values align positionally with card.cols. Each reading is tested against its
  // OWN zone if it has one (col.os / col.ob), otherwise the card's global pair
  // (D-120). `need` still counts against all columns so a null reading can't
  // satisfy an "all" rule.
  let present = 0, lows = 0, highs = 0;
  card.cols.forEach((col, i) => {
    const v = values[i];
    if (v == null) return;
    present++;
    if (v <= (col.os ?? card.os)) lows++;
    if (v >= (col.ob ?? card.ob)) highs++;
  });
  if (!present) return null;
  const need = card.rule === "all" ? card.cols.length : Math.min(card.k, card.cols.length);
  const follow = card.mode === "follow";
  if (lows >= need) return follow ? "sell" : "buy";
  if (highs >= need) return follow ? "buy" : "sell";
  return null;
}

/** The breakout engine's verdict (D-115): the latest close crossing a real SUPPORT /
 * RESISTANCE level. The level is NOT the raw N-bar high/low (a box that can sit on a lone
 * wick the market never respected) — it's the nearest SWING PIVOT the price has actually
 * turned at, found with the same fractal-pivot logic the S/R reading uses, over the last
 * `lookback` bars. A close above the nearest overhead resistance (× 1+buffer) is a buy
 * break; below the nearest support (× 1−buffer) is a sell break; otherwise null. Falls back
 * to the window extreme only when there aren't enough pivots yet. `confirmVal` is the
 * momentum reading's value (0..1) on its own timeframe, or null when there's no confirm /
 * its data isn't ready — a missing confirm is a SKIP. Pure, so the same test runs live and
 * in the base-rate replay. */
export function breakoutVerdict(candles: OHLCPoint[], p: BreakoutParams, confirmVal: number | null): Verdict {
  const N = Math.max(2, Math.round(p.lookback));
  if (candles.length < N + 1) return null;
  const cur = candles[candles.length - 1]!;
  const prior = candles.slice(-N - 1, -1); // the N bars BEFORE the current one
  // Levels = swing pivots (where price reversed) — genuine support/resistance, not the
  // raw high/low. Break = the close clearing the nearest level overhead / underfoot.
  const K = 2;
  const { highs, lows } = swingPivots(prior, K);
  const ref = prior.length ? prior[prior.length - 1]!.close : cur.close;
  const res = highs.filter((h) => h > ref).sort((a, b) => a - b)[0] ?? Math.max(...prior.map((c) => c.high));
  const sup = lows.filter((l) => l < ref).sort((a, b) => b - a)[0] ?? Math.min(...prior.map((c) => c.low));
  if (!(res > sup)) return null;
  const buf = Math.max(0, p.buffer ?? 0);
  const up = cur.close > res * (1 + buf);
  const down = cur.close < sup * (1 - buf);
  if (up === down) return null; // neither (or, impossibly, both) → no break
  if (p.confirm && confirmVal != null) {
    // Momentum must agree with the break direction; a mild default lives in the seed.
    if (up && confirmVal < p.confirm.min) return null;
    if (down && confirmVal > 1 - p.confirm.min) return null;
  }
  return up ? "buy" : "sell";
}

// ---- context gates (volume / volatility) — settings-only, not confluence bars ----

/** Relative volume: latest bar's volume ÷ the average of the prior `window` bars.
 * null when the instrument carries no volume (spot forex / gold) — so a volume gate
 * on such an instrument is skipped, not failed. */
export function relativeVolume(candles: OHLCPoint[], window = 20): number | null {
  if (candles.length < window + 1) return null;
  const last = candles[candles.length - 1]!.volume;
  const prev = candles.slice(-window - 1, -1);
  const sum = prev.reduce((a, c) => a + (c.volume > 0 ? c.volume : 0), 0);
  if (sum <= 0 || last <= 0) return null;
  return last / (sum / prev.length);
}

/** Where current volatility (ATR) sits within its own recent range, 0..1 (0 = the
 * quietest it's been, 1 = the busiest). null when there isn't enough data. */
export function volatilityPercentile(candles: OHLCPoint[], atrPeriod = 14, window = 60): number | null {
  const s = atrSeries(candles, atrPeriod);
  if (s.length < 5) return null;
  const vals = s.slice(-window).map((p) => p.value);
  const last = vals[vals.length - 1]!;
  return clamp01(vals.filter((v) => v <= last).length / vals.length);
}

/** Do a card's context gates pass? Missing context (null) is a SKIP, never a fail —
 * so a gate can't blank an instrument that can't supply its data. */
export function filtersPass(filters: CardFilters | undefined, ctx: { relVol: number | null; volatilityPct: number | null }): boolean {
  if (!filters) return true;
  if (filters.volMin != null && filters.volMin > 0 && ctx.relVol != null && ctx.relVol < filters.volMin) return false;
  if (filters.volatilityMin != null && filters.volatilityMin > 0 && ctx.volatilityPct != null && ctx.volatilityPct < filters.volatilityMin) return false;
  return true;
}

/** Do a setup's reading gates pass? `vals[i]` is gate i's reading value (0..1) or null
 * when its data isn't available. All present gates must sit inside their [min, max];
 * a null value is a SKIP (never a fail), matching the volume/volatility gates. No gates
 * → always true. Pure, so the same check runs live (data layer) and in the base-rate
 * replay (D-115). */
export function gatesPass(gates: ReadingGate[] | undefined, vals: Array<number | null>): boolean {
  if (!gates || !gates.length) return true;
  for (let i = 0; i < gates.length; i++) {
    const v = vals[i];
    if (v == null) continue;
    const g = gates[i]!;
    if (g.min != null && v < g.min) return false;
    if (g.max != null && v > g.max) return false;
  }
  return true;
}

/** Drop exact-duplicate columns (same reading × timeframe). */
export function dedupeColumns(card: Card): void {
  const seen = new Set<string>();
  card.cols = card.cols.filter((c) => {
    const k = c.reading + "|" + c.tf;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
/** The first reading × timeframe not already present, so "+ add" always adds something new. */
export function freshColumn(card: Card): Column {
  for (const r of READING_KEYS) for (const tf of TIMEFRAMES) if (!card.cols.some((c) => c.reading === r && c.tf === tf)) return { reading: r, tf };
  return { reading: "bb", tf: "1D" };
}

/** A card's identity ignoring its id — two cards with the same key are duplicates. */
export function cardKey(c: Card): string {
  // name/dir are included so two differently-labelled trade-type setups on one
  // security never collapse just because their untuned configs coincide. Both are
  // optional and JSON.stringify omits undefined, so nameless legacy cards keep
  // their exact previous key — no migration churn.
  return JSON.stringify({ s: c.sym, c: c.cols, r: c.rule, k: c.k, o: c.os, b: c.ob, n: c.name, d: c.dir, m: c.mode, e: c.engine, bo: c.breakout });
}
/** Collapse exactly-identical cards, and keep cards for the same security adjacent
 * (in order of the security's first appearance). */
export function normalizeBoard(board: Board): Board {
  const seen = new Set<string>();
  const uniq: Board = [];
  for (const c of board) {
    const k = cardKey(c);
    if (seen.has(k)) continue;
    seen.add(k);
    uniq.push(c);
  }
  const order: string[] = [];
  const groups: Record<string, Board> = {};
  for (const c of uniq) {
    if (!groups[c.sym]) {
      groups[c.sym] = [];
      order.push(c.sym);
    }
    groups[c.sym]!.push(c);
  }
  return order.reduce<Board>((acc, s) => acc.concat(groups[s]!), []);
}
